import { useAuth } from '@/backend/context/auth-management';
import { ROUTES } from '@/lib/routes';
import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { AnalyticsService } from '@/shared/analytics/analytics-service';
import { PostHogPageView } from '@/components/providers/posthog-pageview';

export const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // 1. Resetear el scroll global
    window.scrollTo(0, 0);
    document.documentElement.scrollTo(0, 0);
    document.body.scrollTo(0, 0);

    // 2. Resetear contenedores específicos de scroll
    // Buscamos elementos que usualmente usamos en layouts de dashboards
    const scrollableContainers = document.querySelectorAll(
      '.overflow-y-auto, main, #root > div',
    );
    scrollableContainers.forEach((container) => {
      container.scrollTo(0, 0);
    });
  }, [pathname]);

  return null;
};

export default function AuthLayout() {
  const profile = useAuth((s) => s.profile);
  const user = useAuth((s) => s.user);
  const status = useAuth((s) => s.status);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If it's still idle or loading, do nothing yet
    if (status === 'loading' || status === 'idle') {
      return;
    }

    const isLoginPage = location.pathname === ROUTES.PAGE.LOGIN;

    // 1. If we know the user is NOT logged in -> restrict them to the login page
    if (!user || status === 'unauthenticated') {
      if (!isLoginPage) {
        navigate(ROUTES.PAGE.LOGIN, { replace: true });
      }
      return;
    }

    // 3. If logged in, has profile, but trying to view the login page -> send to dashboard
    if (user && profile) {
      AnalyticsService.identify(profile.id, {
        email: profile.email,
        name: profile.public.branding.name,
        account_type: profile.account_type,
      });

      // Set global context for all subsequent events in this session
      AnalyticsService.setGlobalContext({
        salonId: profile.id,
        business_slug: profile.public.branding.slug,
        account_type: profile.account_type,
        $host: process.env.NODE_ENV === 'development' ? 'localhost:3000' : 'app.tufirma.pro',
      });
      
      if (isLoginPage) {
        const target = ROUTES.DASHBOARD.PRINCIPAL;
        navigate(target, { replace: true });
      }
    }
  }, [status, user, profile, navigate, location.pathname]);

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="dark bg-background flex min-h-screen flex-col items-center justify-center">
        <div className="flex flex-col items-center gap-6">
          <div className="relative">
            <div className="bg-ring/20 absolute inset-0 animate-pulse rounded-full blur-xl" />
            <img
              src="/logo.svg"
              alt="Cargando TuFirma..."
              className="relative z-10 h-24 animate-pulse drop-shadow-sm"
            />
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error' || (user && !profile)) {
    // Auth failed or profile missing — force re-login
    return (
      <div className="dark bg-background flex min-h-screen flex-col items-center justify-center gap-6 px-6">
        <div className="relative">
          <div className="bg-ring/20 absolute inset-0 animate-pulse rounded-full blur-xl" />
          <img
            src="/logo.svg"
            alt="TuFirma"
            className="relative z-10 h-24 drop-shadow-sm"
          />
        </div>
        <div className="text-center space-y-3">
          <p className="text-sm font-black uppercase italic text-zinc-400">
            Error al cargar tu perfil
          </p>
          <button
            onClick={() => window.location.reload()}
            className="h-12 rounded-2xl bg-zinc-900 px-6 text-[10px] font-black uppercase tracking-widest text-white shadow-xl active:scale-95 transition-all"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <PostHogPageView />
      <Outlet />
    </>
  );
}
